import { buildUserAgent, fetchWithTimeout, throwForHttpStatus } from '@wport/core';
import { CliError, ExitCode } from './errors';
import { OAUTH_BASE, type DeviceCodeResponse, type TokenResponse } from '@wport/core';
import { CLI_SOURCE } from './global-opts';

export interface OauthRequestOptions {
	baseUrl: string; // resolveContext 產出，已去尾斜線
	locale: string;
	timeoutMs: number;
}

const EXPIRED_MESSAGE = 'The device code expired before authorization completed. Run `wport login` to try again.';

/**
 * OAuth 端點手寫 wrapper。與 enterprise-client / personal-client 不同：這幾支端點不帶
 * Authorization（device_code / refresh_token 本身就是憑證，回應也是 RFC 標準 raw JSON，
 * 無 W101 慣用的 DataResponse wrapper）。刻意「不」在這裡就地丟錯 —— 呼叫端各自的狀態機
 * （尤其 pollForToken 的四種 device-grant 錯誤碼）需要親眼看到 status/body 才能分派，
 * 提早丟錯會讓 caller 沒機會做狀態機判斷。
 */
async function oauthPost(
	opts: OauthRequestOptions,
	path: string,
	body: Record<string, unknown>
): Promise<{ status: number; body: unknown }> {
	const url = new URL(`${opts.baseUrl}${path}`);
	const request = new Request(url, {
		method: 'POST',
		headers: {
			'Accept-Language': opts.locale,
			'User-Agent': buildUserAgent('wport-cli', __CLI_VERSION__),
			'X-Source': CLI_SOURCE,
			Accept: 'application/json',
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(body),
	});
	const res = await fetchWithTimeout(request, opts.timeoutMs);
	const respBody: unknown = await res.json().catch(() => null);
	return { status: res.status, body: respBody };
}

/** OAuth 錯誤 body 恆為 RFC 標準 `{ error: string }`（design doc §3.5）；缺席回 null 讓呼叫端 fallback。 */
function oauthErrorCode(body: unknown): string | null {
	if (body && typeof body === 'object' && typeof (body as Record<string, unknown>).error === 'string') {
		return (body as Record<string, unknown>).error as string;
	}
	return null;
}

/** `POST /oauth/device/code`。deviceName 為 null/空字串時不帶該欄位（後端視為未命名裝置）。 */
export async function requestDeviceCode(opts: OauthRequestOptions, deviceName: string | null): Promise<DeviceCodeResponse> {
	const body: Record<string, unknown> = {};
	if (deviceName) body.device_name = deviceName;
	const { status, body: respBody } = await oauthPost(opts, `${OAUTH_BASE}/device/code`, body);
	if (status < 200 || status >= 300) throwForHttpStatus(status, respBody);
	return respBody as DeviceCodeResponse;
}

/**
 * RFC 8628 輪詢狀態機。interval 起始值 = `device.interval`（秒）；`slow_down` 時 interval +5 秒
 * 續輪，往後沿用新值；`authorization_pending` 用當前 interval 續輪；`access_denied` /
 * `expired_token` 直接 CliError exit 3。第一次嘗試不 sleep —— 使用者剛看到 user_code / 瀏覽器
 * 才要開，讓第一次輪詢立即發生沒有意義上的壞處，只有「這次沒過」才需要等待再試。
 *
 * 額外的本地 deadline（`device.expires_in` 秒）是防禦性判斷：伺服器理論上會在裝置碼過期後
 * 回 `expired_token`，但避免因網路延遲、殘留連線等情況導致無窮迴圈，仍在本機端加一道保險，
 * 觸發時視同 expired 處理。sleep 可注入，測試斷言呼叫次數與間隔（預設值才是正式環境的真等待）。
 */
export async function pollForToken(
	opts: OauthRequestOptions,
	device: DeviceCodeResponse,
	sleep: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
): Promise<TokenResponse> {
	let intervalSec = device.interval;
	const deadline = Date.now() + device.expires_in * 1000;

	for (;;) {
		const { status, body } = await oauthPost(opts, `${OAUTH_BASE}/token`, {
			grant_type: 'urn:ietf:params:oauth:grant-type:device_code',
			device_code: device.device_code,
		});
		if (status >= 200 && status < 300) return body as TokenResponse;

		const code = oauthErrorCode(body);
		if (code === 'slow_down') {
			intervalSec += 5;
		} else if (code === 'access_denied') {
			throw new CliError('Authorization was denied on the device.', ExitCode.ServerClientError);
		} else if (code === 'expired_token') {
			throw new CliError(EXPIRED_MESSAGE, ExitCode.ServerClientError);
		} else if (code !== 'authorization_pending') {
			// 未知碼（理論上不該出現在 device grant 的 invalid_grant 等）：無法辨識就走通用 HTTP 錯誤路徑，
			// 400 經 throwForHttpStatus 一樣落在 exit 3，行為與明確分派的四碼一致。
			throwForHttpStatus(status, body);
		}

		if (Date.now() >= deadline) throw new CliError(EXPIRED_MESSAGE, ExitCode.ServerClientError);
		await sleep(intervalSec * 1000);
	}
}

/** `POST /oauth/token`(refresh grant)。`invalid_grant` 涵蓋單純過期與 reuse-detection 全撤兩種情境，訊息統一導向重新登入。 */
export async function refreshAccessToken(opts: OauthRequestOptions, refreshToken: string): Promise<TokenResponse> {
	const { status, body } = await oauthPost(opts, `${OAUTH_BASE}/token`, {
		grant_type: 'refresh_token',
		refresh_token: refreshToken,
	});
	if (status >= 200 && status < 300) return body as TokenResponse;
	if (oauthErrorCode(body) === 'invalid_grant') {
		throw new CliError('Your session is no longer valid. Run `wport login` to sign in again.', ExitCode.ServerClientError);
	}
	throwForHttpStatus(status, body);
}

/** `POST /oauth/revoke`（RFC 7009）。契約上一律 200 空物件、無 revoke oracle，這裡仍對非 2xx 做防禦性處理。 */
export async function revokeRefreshToken(opts: OauthRequestOptions, refreshToken: string): Promise<void> {
	const { status, body } = await oauthPost(opts, `${OAUTH_BASE}/revoke`, { token: refreshToken });
	if (status < 200 || status >= 300) throwForHttpStatus(status, body);
}
